import { useState } from 'react';
import StatusBadge from '../StatusBadge';
import Modal from '../Modal';
import complianceService from '../../services/compliance.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { colors, gradients } from '../../styles/theme';
import type { ComplianceState } from '../../types/compliance';
import {
  KYC_EXPORT_REASON_MIN,
  PERSONAL_EXPORT_REFERENCE_MIN,
  canExportKycFile,
  canExportPersonalData,
  deathNoticeView,
  exportBlockedReason,
  recordBlockedReason,
  releaseBlockedReason,
  saveBlob,
} from './clientRecords.helpers';

/**
 * Rows on the client compliance panel for a reported death and for the two audited
 * exports of a client's personal data. Each control is offered only to the role the
 * API accepts for it.
 *
 *  · Death notice — recording freezes the account; releasing it is four-eyes (the
 *                   releaser must be a different person than the one who recorded it).
 *  · KYC file     — the regulator expediente (super_admin).
 *  · Personal data — the answer to a data-subject access request (manager, super_admin).
 */

type Dialog = 'record_death' | 'release_death' | 'kyc_export' | 'personal_export';

const FIELD_STYLE = { border: `1px solid ${colors.border}` };
const LABEL_CLASS = 'block text-xs font-medium mb-1';

export function RecordRow({
  label, badge, detail, children,
}: Readonly<{
  label: string; badge?: JSX.Element; detail: string; children?: React.ReactNode;
}>): JSX.Element {
  return (
    <div
      className="flex items-center justify-between gap-3 py-3"
      style={{ borderTop: `1px solid ${colors.borderSoft}` }}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ color: colors.ink }}>{label}</span>
          {badge}
        </div>
        <div className="text-xs mt-0.5" style={{ color: colors.muted }}>{detail}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">{children}</div>
    </div>
  );
}

export function RowButton({
  label, onClick, danger = false,
}: Readonly<{ label: string; onClick: () => void; danger?: boolean }>): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3 py-1.5 rounded-md text-xs font-semibold"
      style={
        danger
          ? { background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }
          : { border: `1px solid ${colors.border}`, color: colors.inkSoft }
      }
    >
      {label}
    </button>
  );
}

interface RowsProps {
  clientId: string;
  role: string | undefined;
  /** The id of whoever is signed in: a notice is released by someone other than its recorder. */
  viewerId: string | undefined;
  /** The last compliance state that could be read; null when it could not be. */
  state: ComplianceState | null;
  onDone: (msg: string) => void;
}

export function ClientRecordsRows(
  { clientId, role, viewerId, state, onDone }: Readonly<RowsProps>,
): JSX.Element {
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const view = deathNoticeView(state, viewerId);
  const close = (): void => setDialog(null);
  const done = (msg: string): void => {
    setDialog(null);
    onDone(msg);
  };

  let deathBadge: JSX.Element;
  let deathDetail: string;
  if (view.kind === 'unreadable') {
    deathBadge = <StatusBadge tone="warning">Unknown</StatusBadge>;
    deathDetail = 'The current standing could not be read. Reload the page to check before acting.';
  } else if (view.kind === 'active') {
    deathBadge = <StatusBadge tone="danger">Reported</StatusBadge>;
    deathDetail = view.recordedByViewer
      ? 'A death report is in force. You recorded it, so a different manager must release it.'
      : 'A death report is in force. A different manager than the one who recorded it must release it.';
  } else {
    deathBadge = <StatusBadge tone="info">None</StatusBadge>;
    deathDetail = 'No death report is in force for this client.';
  }

  return (
    <>
      <RecordRow label="Death notice" badge={deathBadge} detail={deathDetail}>
        {/* The recorder is not offered a release the API would refuse: the row says who must. */}
        {view.kind === 'active' && !view.recordedByViewer && (
          <RowButton label="Release notice" onClick={() => setDialog('release_death')} />
        )}
        {view.kind === 'none' && (
          <RowButton label="Record death notice" danger onClick={() => setDialog('record_death')} />
        )}
      </RecordRow>

      {canExportPersonalData(role) && (
        <RecordRow
          label="Personal data export"
          detail="Download the client's own data as a ZIP, to answer a data-subject access request."
        >
          <RowButton label="Export personal data" onClick={() => setDialog('personal_export')} />
        </RecordRow>
      )}

      {canExportKycFile(role) && (
        <RecordRow
          label="KYC file export"
          detail="Download the client's complete KYC file as a ZIP, for a regulator or audit request."
        >
          <RowButton label="Export KYC file" onClick={() => setDialog('kyc_export')} />
        </RecordRow>
      )}

      {dialog === 'record_death' && (
        <RecordDeathModal clientId={clientId} onClose={close} onDone={done} />
      )}
      {dialog === 'release_death' && (
        <ReleaseDeathModal clientId={clientId} onClose={close} onDone={done} />
      )}
      {dialog === 'kyc_export' && (
        <ExportModal kind="kyc" clientId={clientId} onClose={close} onDone={done} />
      )}
      {dialog === 'personal_export' && (
        <ExportModal kind="personal" clientId={clientId} onClose={close} onDone={done} />
      )}
    </>
  );
}

// ─── Shared dialog pieces ─────────────────────────────────────────
export function ErrorBox({ message }: Readonly<{ message: string | null }>): JSX.Element | null {
  if (!message) return null;
  return (
    <div
      role="alert"
      className="mb-4 p-3 rounded-lg text-sm"
      style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}
    >
      {message}
    </div>
  );
}

interface FooterProps {
  busy: boolean;
  blocked: string | null;
  cta: string;
  danger?: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

export function DialogFooter(
  { busy, blocked, cta, danger = false, onClose, onSubmit }: Readonly<FooterProps>,
): JSX.Element {
  const off = blocked !== null || busy;
  return (
    <>
      {blocked && (
        <p className="mb-3 text-xs" style={{ color: colors.muted }}>{blocked}</p>
      )}
      <div className="flex justify-end gap-2">
        <button
          type="button" onClick={onClose} disabled={busy}
          className="px-4 py-2 rounded-lg text-sm"
          style={{ border: `1px solid ${colors.border}`, color: colors.inkSoft }}
        >
          Cancel
        </button>
        <button
          type="button" onClick={onSubmit} disabled={off}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{
            background: danger ? colors.danger : gradients.gold,
            color: danger ? '#fff' : colors.navy,
            opacity: off ? 0.5 : 1,
          }}
        >
          {busy ? 'Working…' : cta}
        </button>
      </div>
    </>
  );
}

interface DialogProps {
  clientId: string;
  onClose: () => void;
  onDone: (msg: string) => void;
}

// ─── Record a death notice ────────────────────────────────────────
export function RecordDeathModal({ clientId, onClose, onDone }: Readonly<DialogProps>): JSX.Element {
  const [reporter, setReporter] = useState('');
  const [notes, setNotes] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const blocked = recordBlockedReason(confirmed);

  const submit = async (): Promise<void> => {
    if (blocked) return;
    setBusy(true);
    setErr(null);
    try {
      await complianceService.recordDeathNotice(clientId, {
        reporterDescription: reporter.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      onDone('Death notice recorded. The account is frozen; a different manager must release it if the report is rejected.');
    } catch (e: unknown) {
      setErr(getApiErrorMessage(e, 'Could not record the death notice.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} busy={busy} maxWidth="lg">
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>Record death notice</h2>
      <p className="text-sm mb-4" style={{ color: colors.danger }}>
        This records that the client has been reported as deceased and freezes the account,
        so no deposit or withdrawal can be approved. Nothing is deleted. The notice cannot be
        edited; if the report is wrong, a different manager must release it.
      </p>
      <label htmlFor="death-reporter" className={LABEL_CLASS} style={{ color: colors.muted }}>
        Who reported it and how (optional)
      </label>
      <textarea
        id="death-reporter" value={reporter} rows={2} maxLength={2000}
        onChange={(e) => setReporter(e.target.value)}
        className="w-full px-3 py-2 rounded-lg text-sm mb-3" style={FIELD_STYLE}
      />
      <label htmlFor="death-notes" className={LABEL_CLASS} style={{ color: colors.muted }}>
        Notes (optional)
      </label>
      <textarea
        id="death-notes" value={notes} rows={3} maxLength={2000}
        onChange={(e) => setNotes(e.target.value)}
        className="w-full px-3 py-2 rounded-lg text-sm mb-4" style={FIELD_STYLE}
      />
      <label className="flex items-start gap-2 mb-4 text-sm" style={{ color: colors.ink }}>
        <input
          id="death-confirm" type="checkbox" checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)} className="mt-1"
        />
        <span>I confirm this report is genuine and I understand it freezes this client&apos;s account.</span>
      </label>
      <ErrorBox message={err} />
      <DialogFooter
        busy={busy} blocked={blocked} cta="Record and freeze" danger
        onClose={onClose} onSubmit={submit}
      />
    </Modal>
  );
}

// ─── Release a death notice ───────────────────────────────────────
export function ReleaseDeathModal({ clientId, onClose, onDone }: Readonly<DialogProps>): JSX.Element {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const blocked = releaseBlockedReason(reason);

  const submit = async (): Promise<void> => {
    if (blocked) return;
    setBusy(true);
    setErr(null);
    try {
      await complianceService.releaseDeathNotice(clientId, reason.trim());
      onDone('Death notice released. Its freeze is lifted; any other hold stays.');
    } catch (e: unknown) {
      setErr(getApiErrorMessage(e, 'Could not release the death notice.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} busy={busy} maxWidth="lg">
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>Release death notice</h2>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        Use this when the report is rejected or the client has shown they are alive. It lifts
        the freeze that the notice placed and restores money movement; any other hold on the
        account stays. Only a different manager than the one who recorded the notice can do
        this, and you will be told if that is you.
      </p>
      <label htmlFor="death-release-reason" className={LABEL_CLASS} style={{ color: colors.muted }}>
        Reason (kept in the audit log)
      </label>
      <textarea
        id="death-release-reason" value={reason} rows={3} maxLength={2000}
        onChange={(e) => setReason(e.target.value)}
        className="w-full px-3 py-2 rounded-lg text-sm mb-4" style={FIELD_STYLE}
      />
      <ErrorBox message={err} />
      <DialogFooter
        busy={busy} blocked={blocked} cta="Release notice"
        onClose={onClose} onSubmit={submit}
      />
    </Modal>
  );
}

// ─── Audited exports ──────────────────────────────────────────────
interface ExportModalProps extends DialogProps {
  kind: 'kyc' | 'personal';
}

const EXPORT_COPY = {
  kyc: {
    title: 'Export KYC file',
    blurb:
      "Downloads the client's complete KYC file, including personal data in clear text, for a regulator or audit request. Each export is recorded in the audit log with the reason you give.",
    fieldLabel: 'Reason for the export (kept in the audit log)',
    what: 'a reason',
    min: KYC_EXPORT_REASON_MIN,
    max: 2000,
    cta: 'Download KYC file',
    failure: 'Could not export the KYC file.',
    done: 'KYC file downloaded.',
  },
  personal: {
    title: 'Export personal data',
    blurb:
      "Downloads the client's own data, without internal controls or other people's identities, to answer a data-subject access request. Each export is recorded in the audit log with the reference you give.",
    fieldLabel: "Reference of the client's request (kept in the audit log)",
    what: 'a request reference',
    min: PERSONAL_EXPORT_REFERENCE_MIN,
    max: 120,
    cta: 'Download personal data',
    failure: 'Could not export the personal data.',
    done: 'Personal data downloaded.',
  },
} as const;

export function ExportModal(
  { kind, clientId, onClose, onDone }: Readonly<ExportModalProps>,
): JSX.Element {
  const copy = EXPORT_COPY[kind];
  const [text, setText] = useState('');
  const [includeDocs, setIncludeDocs] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const blocked = exportBlockedReason(text, copy.min, copy.what);

  const submit = async (): Promise<void> => {
    if (blocked) return;
    setBusy(true);
    setErr(null);
    try {
      const file = kind === 'kyc'
        ? await complianceService.exportKycFile(clientId, text.trim(), includeDocs)
        : await complianceService.exportPersonalData(clientId, text.trim(), includeDocs);
      saveBlob(file.blob, file.filename);
      onDone(copy.done);
    } catch (e: unknown) {
      setErr(getApiErrorMessage(e, copy.failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} busy={busy} maxWidth="lg">
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>{copy.title}</h2>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>{copy.blurb}</p>
      <label htmlFor="export-text" className={LABEL_CLASS} style={{ color: colors.muted }}>
        {copy.fieldLabel}
      </label>
      <textarea
        id="export-text" value={text} rows={kind === 'kyc' ? 3 : 1} maxLength={copy.max}
        onChange={(e) => setText(e.target.value)}
        className="w-full px-3 py-2 rounded-lg text-sm mb-3" style={FIELD_STYLE}
      />
      <label className="flex items-start gap-2 mb-4 text-sm" style={{ color: colors.ink }}>
        <input
          id="export-docs" type="checkbox" checked={includeDocs}
          onChange={(e) => setIncludeDocs(e.target.checked)} className="mt-1"
        />
        <span>Include the uploaded documents (a very large file may be refused; untick to export without them)</span>
      </label>
      <ErrorBox message={err} />
      <DialogFooter busy={busy} blocked={blocked} cta={copy.cta} onClose={onClose} onSubmit={submit} />
    </Modal>
  );
}
